import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { ToggleRight, ToggleLeft, Check, Save, Loader } from 'lucide-react'
import { getOrderingPermissions, saveOrderingPermissions } from '../../lib/publicData'
import { BUSINESSES as FALLBACK_BUSINESSES } from '../../data/orderMenu'
import { ensureMenuSeeded } from '../../lib/customerMenuService'

const DEPT_BIZ_KEY_MAP = {
  kitchen: 'desres',
  bakery: 'bakery',
  tuckshop: 'desres',
  yawehs: 'yawehs',
}

export default function OrderSettingsTab({ addToast, department }) {
  const deptBizKey = DEPT_BIZ_KEY_MAP[department]
  const [permissions, setPermissions] = useState({})
  const [menuBusinesses, setMenuBusinesses] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    Promise.all([
      getOrderingPermissions(),
      ensureMenuSeeded(),
    ]).then(([p, biz]) => {
      setPermissions(p)
      setMenuBusinesses(biz || FALLBACK_BUSINESSES)
      setLoading(false)
    }).catch(err => {
      console.warn('[Admin] ordering-permissions load failed:', err?.message)
      Sentry.captureException(err, { extra: { fn: 'Admin.OrderSettingsTab.init' } })
      setLoading(false)
    })
  }, [])

  const permKey = (bizKey, catKey) => `${bizKey}__${catKey}`
  const isEnabled = (bizKey, catKey) => permissions[permKey(bizKey, catKey)] !== false
  const toggle = (bizKey, catKey) => {
    const key = permKey(bizKey, catKey)
    setPermissions(prev => ({ ...prev, [key]: prev[key] === false ? true : false }))
  }

  const [saveStatus, setSaveStatus] = useState(null)

  const handleSave = async () => {
    setSaving(true)
    setSaveStatus(null)
    try {
      const result = await Promise.race([
        saveOrderingPermissions(permissions).then(() => 'ok'),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000)),
      ])
      if (result === 'ok') {
        setSaveStatus('saved')
        addToast('Permissions saved', 'success')
        setTimeout(() => setSaveStatus(null), 3000)
      }
    } catch (err) {
      setSaveStatus('failed')
      const msg = err?.message === 'timeout'
        ? 'Save failed — check your connection and try again'
        : 'Failed to save permissions'
      addToast(msg, 'error')
    }
    setSaving(false)
  }

  if (loading) return <p className="text-gray-500 text-sm text-center py-8">Loading...</p>

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h3 className="text-sm font-semibold text-white mb-1">Ordering Permissions</h3>
        <p className="text-xs text-gray-500 mb-6">Toggle which menu categories customers can order from. Disabled categories will appear grayed out on the public ordering page.</p>
      </div>
      {menuBusinesses.filter(biz => !deptBizKey || biz.key === deptBizKey).map(biz => (
        <div key={biz.key} className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
          <div className="px-5 py-3 bg-gray-800/80 border-b border-gray-700">
            <h4 className="font-medium text-white text-sm">{biz.label}</h4>
          </div>
          <div className="divide-y divide-gray-700/50">
            {biz.categories.map(cat => (
              <div key={cat.key} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-200">{cat.label}</p>
                  <p className="text-[10px] text-gray-500">{cat.items.length} items{cat.externalLink ? ' — external link' : ''}</p>
                </div>
                <button onClick={() => toggle(biz.key, cat.key)} className="shrink-0 ml-4">
                  {isEnabled(biz.key, cat.key)
                    ? <ToggleRight size={28} className="text-green-400" />
                    : <ToggleLeft size={28} className="text-gray-600" />
                  }
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold ${
            saveStatus === 'saved'
              ? 'bg-green-700 text-white'
              : saveStatus === 'failed'
              ? 'bg-red-700 hover:bg-red-600 text-white'
              : 'bg-brand-600 hover:bg-brand-500 text-white'
          } disabled:opacity-50`}
        >
          {saving ? <Loader size={14} className="animate-spin" /> : saveStatus === 'saved' ? <Check size={14} /> : <Save size={14} />}
          {saving ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'failed' ? 'Retry Save' : 'Save Permissions'}
        </button>
      </div>
    </div>
  )
}
